import { useEffect, useRef, type FC, type SVGProps } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { Avatar, Box, Flex, HStack, Text, VStack } from '@bonusly/design-system';
import { useCurrentCompany, useCurrentUser } from '@bonusly/who-am-i';

import { AvatarGroup } from '../../../design-system/avatar/components/avatar-group';
import type { BizyPeopleBrowserEntry, BizyPeopleSource } from '../../../lib/bizy/types';
import { Bizy } from '../../bizy';
import { GithubLogo } from '../../github/github-logo';
import { GranolaLogo } from '../../granola/granola-logo';
import { LinearLogo } from '../../linear/linear-logo';
import { MsTeamsLogo } from '../../ms-teams/ms-teams-logo';
import { SlackLogo } from '../../slack/slack-logo';
import { bizyGreetingFallbackName, useBizyGreetingDisplayName } from '../hooks/use-bizy-greeting-display-name';
import { useBizySidePanelStore } from '../store/use-bizy-side-panel-store';

// New-segment greeter callout (BIZY-638). Rendered at the bottom of the chat
// timeline when a fresh segment is about to start. When the member has unread
// People Browser moments it summarizes who's worth recognizing and links into
// the People Browser. With no moments it falls back to a low-key "connect your
// tools" empty state (BIZY-700). Dismissal on first message send is owned by the
// chat view (via `useBizyGreeterCallout`).

const SOURCE_LABELS: Record<BizyPeopleSource, string> = {
	bonusly: 'Bonusly',
	slack: 'Slack',
	github: 'GitHub',
	ms_teams: 'Microsoft Teams',
	granola: 'Granola',
};

const AVATAR_STACK_MAX = 2;

// The five Bizy connectors, in display order, each gated on a company flag. The
// "Add connector" button shows the logos of those enabled for the company.
const CONNECTORS: { key: string; flag: string; Logo: FC<SVGProps<SVGSVGElement>> }[] = [
	{ key: 'granola', flag: 'bizy_connectors_granola', Logo: GranolaLogo },
	{ key: 'linear', flag: 'bizy_connectors_linear', Logo: LinearLogo },
	{ key: 'github', flag: 'bizy_connectors_github', Logo: GithubLogo },
	{ key: 'slack', flag: 'bizy_connectors_slack', Logo: SlackLogo },
	{ key: 'ms_teams', flag: 'bizy_connectors_ms_teams', Logo: MsTeamsLogo },
];

function firstNameOf(entry: BizyPeopleBrowserEntry): string {
	return entry.person.name?.trim().split(/\s+/)[0] ?? '';
}

/** Human-friendly source list, e.g. "Slack, GitHub, & Bonusly". */
function formatSources(entries: BizyPeopleBrowserEntry[]): string {
	const labels = [
		...new Set(
			entries
				.map((e) => e.source)
				.filter((s): s is BizyPeopleSource => s != null)
				.map((s) => SOURCE_LABELS[s])
		),
	];
	if (labels.length <= 1) return labels[0] ?? '';
	if (labels.length === 2) return `${labels[0]} & ${labels[1]}`;
	return `${labels.slice(0, -1).join(', ')}, & ${labels[labels.length - 1]}`;
}

/** Overlapping cluster of enabled-connector brand marks for the CTA button. */
function ConnectorLogoCluster() {
	const company = useCurrentCompany();
	const enabled = CONNECTORS.filter((c) => Boolean((company as Record<string, unknown> | undefined)?.[c.flag]));
	if (enabled.length === 0) return null;

	return (
		<HStack spacing={0} flexShrink={0}>
			{enabled.map(({ key, Logo }, index) => (
				<Flex
					key={key}
					align="center"
					justify="center"
					boxSize="20px"
					borderRadius="full"
					bg="bg.surface"
					borderWidth="1px"
					borderColor="border.soft"
					color="text.default"
					ml={index === 0 ? 0 : '-6px'}
					zIndex={enabled.length - index}
				>
					<Box as="span" display="inline-flex">
						<Logo width="12" height="12" />
					</Box>
				</Flex>
			))}
		</HStack>
	);
}

export interface BizyGreeterCalloutProps {
	/** Unread People Browser entries (already filtered to unread). */
	unreadEntries: BizyPeopleBrowserEntry[];
	/** Time-of-day i18n key ("greeter.morning" | "greeter.afternoon" | "greeter.evening"). */
	timeOfDayKey: string;
	fontSize?: string | object;
	/**
	 * Total number of People Browser entries (read + unread). The "connect your
	 * tools" nudge text only shows when this is zero — a member with moments
	 * they've already read has working tools, so we must not nudge them (BIZY-700).
	 * Defaults to the unread count when omitted.
	 */
	momentCount?: number;
	/**
	 * Number of connected connectors. Below two, the no-moments empty state
	 * shows the "connect your tools" nudge text (BIZY-700).
	 */
	connectedCount?: number;
	/**
	 * Opens the connectors settings. Defaults to the shared side-panel store's
	 * `showSettings('connections')`; the desktop surface injects its own handler.
	 */
	onAddConnector?: () => void;
}

// Below this many connected connectors we show the connect-your-tools nudge.
const CONNECT_NUDGE_MAX_CONNECTORS = 2;

export function BizyGreeterCallout({ unreadEntries, timeOfDayKey, fontSize, momentCount, connectedCount, onAddConnector }: BizyGreeterCalloutProps) {
	const { t } = useTranslation('admin_bizy');
	const { track } = useAnalytics();
	const user = useCurrentUser();
	const viewPerson = useBizySidePanelStore((state) => state.viewPerson);
	const showSettings = useBizySidePanelStore((state) => state.showSettings);
	// Nickname-preferring greeting name, matching the fresh-start greeting and
	// Bizy Desktop: the Bizy personalization nickname when set, else the shared
	// surface fallback.
	const greetingName = useBizyGreetingDisplayName(bizyGreetingFallbackName(user));

	const unreadCount = unreadEntries.length;
	const firstUnread = unreadEntries[0];
	// The people summary is driven by unread entries; the empty-state nudge keys
	// off total moments so a member who has read their moments isn't nudged.
	const hasUnread = unreadCount > 0 && Boolean(firstUnread);
	const hasMoments = (momentCount ?? unreadCount) > 0;

	// The callout is about *people*, so dedupe repeat moments by person. Names,
	// the "N people" count, and the avatar stack all derive from this unique set.
	// The backend already returns one entry per person; this stays as a guard.
	const uniquePeople: BizyPeopleBrowserEntry[] = [];
	const seenPersonIds = new Set<string>();
	for (const entry of unreadEntries) {
		if (seenPersonIds.has(entry.person.id)) continue;
		seenPersonIds.add(entry.person.id);
		uniquePeople.push(entry);
	}
	const peopleCount = uniquePeople.length;

	const hasTrackedShownRef = useRef(false);
	useEffect(() => {
		if (hasTrackedShownRef.current) return;
		hasTrackedShownRef.current = true;
		if (hasUnread) {
			track(AnalyticsEvent.Bizy.GreeterShown, { unreadCount });
		} else {
			track(AnalyticsEvent.Bizy.GreeterEmptyShown, { connectedCount });
		}
	}, [hasUnread, unreadCount, connectedCount, track]);

	const greetingLine = t('greeter.greetingLine', { greeting: t(timeOfDayKey), name: greetingName });

	const handleAddConnector = () => {
		track(AnalyticsEvent.Bizy.GreeterAddConnectorClicked, { connectedCount });
		if (onAddConnector) onAddConnector();
		else showSettings('connections');
	};

	const addConnectorButton = (
		<Box
			as="button"
			type="button"
			onClick={handleAddConnector}
			aria-label={t('greeter.addConnector')}
			borderWidth="1px"
			borderColor="border.soft"
			bg="bg.surface"
			borderRadius="md"
			px={3}
			py={1.5}
			transitionProperty="common"
			transitionDuration="normal"
			_hover={{ borderColor: 'border.emphasis', bg: 'bg.muted' }}
		>
			<HStack spacing={2} align="center">
				<ConnectorLogoCluster />
				<Text fontSize="sm" fontWeight="medium" color="text.default">
					{t('greeter.addConnector')}
				</Text>
			</HStack>
		</Box>
	);

	// ── No-moments empty state (BIZY-700) ──────────────────────────────
	if (!hasUnread) {
		// Only nudge to connect tools when there are truly no moments; a member
		// with read-only moments has working tools.
		const showNudgeText = !hasMoments && (connectedCount == null || connectedCount < CONNECT_NUDGE_MAX_CONNECTORS);

		return (
			<Flex direction="column" align="center" gap={3} py="180px" maxW="440px" w="100%" mx="auto" data-component="bizy-greeter-callout">
				<Bizy state="waving" size="md" />

				<Text fontSize="md" fontWeight="semibold" color="text.default" textAlign="center">
					{greetingLine}
				</Text>

				{showNudgeText && (
					<Text fontSize={fontSize ?? 'sm'} color="text.soft" textAlign="center" maxW="360px" lineHeight="tall">
						{t('greeter.readyToHelp')}
					</Text>
				)}

				{addConnectorButton}
			</Flex>
		);
	}

	const leadNames = uniquePeople.slice(0, AVATAR_STACK_MAX).map(firstNameOf).filter(Boolean);
	const remaining = peopleCount - leadNames.length;
	const namesSummary = remaining > 0 ? t('greeter.namesAndOthers', { names: leadNames.join(', '), count: remaining }) : leadNames.join(', ');
	const sources = formatSources(unreadEntries);

	const handleCta = () => {
		track(AnalyticsEvent.Bizy.GreeterCtaClicked, { unreadCount });
		// Open the first unseen person; the person view marks the moment seen on view.
		viewPerson({ id: firstUnread.person.id, name: firstUnread.person.name ?? '', observedMomentId: firstUnread.observedMomentId });
	};

	return (
		<Flex direction="column" align="center" gap={3} py="116px" maxW="440px" w="100%" mx="auto" data-component="bizy-greeter-callout">
			<Bizy state="waving" size="md" />

			<Text fontSize="md" fontWeight="semibold" color="text.default" textAlign="center">
				{greetingLine}
			</Text>

			<Text fontSize={fontSize ?? 'sm'} color="text.soft" textAlign="center" maxW="320px" lineHeight="tall">
				<Trans
					t={t}
					i18nKey="greeter.intro"
					count={peopleCount}
					values={{ count: peopleCount }}
					components={[
						<Box
							key="cta"
							as="button"
							type="button"
							color="text.interactive"
							fontWeight="medium"
							onClick={handleCta}
							aria-label={t('greeter.ctaAriaLabel')}
							sx={{ display: 'inline', textAlign: 'left', _hover: { textDecoration: 'underline' } }}
						/>,
					]}
				/>
			</Text>

			<Box
				as="button"
				type="button"
				onClick={handleCta}
				aria-label={t('greeter.ctaAriaLabel')}
				borderWidth="1px"
				borderColor="border.soft"
				bg="bg.surface"
				borderRadius="md"
				px={3}
				py={2}
				w="100%"
				transitionProperty="common"
				transitionDuration="normal"
				_hover={{ borderColor: 'border.emphasis', bg: 'bg.muted' }}
			>
				<HStack spacing={3} align="center">
					<AvatarGroup max={AVATAR_STACK_MAX} size="sm">
						{uniquePeople.map((entry) => (
							<Avatar key={entry.person.id} name={entry.person.name ?? undefined} src={entry.person.avatarUrl ?? undefined} size="sm" />
						))}
					</AvatarGroup>
					<VStack align="stretch" spacing={0} minW={0} flex={1}>
						<Text fontSize="sm" fontWeight="semibold" color="text.default" noOfLines={1} textAlign="left">
							{namesSummary}
						</Text>
						{sources && (
							<Text fontSize="xs" color="text.muted" noOfLines={1} textAlign="left">
								{t('greeter.spottedAcross', { sources, interpolation: { escapeValue: false } })}
							</Text>
						)}
					</VStack>
				</HStack>
			</Box>

			{/* Stricter than the no-moments empty state's nudge threshold above
			    (< CONNECT_NUDGE_MAX_CONNECTORS): a member who already has unread
			    moments has proven at least one connector is working, so this only
			    nudges when there's truly nothing connected yet. */}
			{!connectedCount && addConnectorButton}
		</Flex>
	);
}
